/**
 * GEDCOM Parser Utility
 * Parses GEDCOM (.ged) genealogy files and extracts family tree data
 */

export const parseGedFile = (gedContent) => {
    const lines = gedContent.split("\n");
    const individuals = {};
    const families = {};
    let currentRecord = null;
    let nameBeingProcessed = false;
    let currentEventType = null; // Track if we're parsing BIRT or DEAT

    lines.forEach((line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        const parts = trimmedLine.split(" ");
        const level = parseInt(parts[0], 10);
        const tag = parts[1];
        const value = parts.slice(2).join(" ");

        // Parse individual properties at level 1
        if (tag && tag.startsWith("@") && tag.endsWith("@") && value === "INDI") {
            const id = tag.replace(/@/g, "");
            currentRecord = { id, type: "INDI", data: {} };
            individuals[id] = currentRecord.data;
            nameBeingProcessed = false;
            currentEventType = null; // Reset event type for new record
        }
        // Parse family records: 0 @F0000@ FAM
        // Format: level tag(id) recordType
        else if (tag && tag.startsWith("@") && tag.endsWith("@") && value === "FAM") {
            const id = tag.replace(/@/g, "");
            currentRecord = { id, type: "FAM", data: {} };
            families[id] = currentRecord.data;
            nameBeingProcessed = false;
            currentEventType = null; // Reset event type for new record
        }
        // Parse individual properties at level 1
        else if (currentRecord && currentRecord.type === "INDI" && level === 1) {
            if (tag === "NAME") {
                nameBeingProcessed = true;
                currentEventType = null; // Not an event
                const nameParts = value.split("/");
                const givenName = nameParts[0].trim();
                const surname = nameParts[1] ? nameParts[1].trim() : "";
                const fullName = value.replace(/\//g, " ").trim();

                // Initialize or update name (prefer birth names)
                if (!currentRecord.data.fullName) {
                    currentRecord.data.fullName = fullName;
                    currentRecord.data.givenName = givenName;
                    currentRecord.data.surname = surname;
                }
            } else if (tag === "SEX") {
                currentRecord.data.sex = value;
                nameBeingProcessed = false;
                currentEventType = null;
            } else if (tag === "BIRT") {
                currentRecord.data.birthData = {};
                nameBeingProcessed = false;
                currentEventType = "BIRT"; // Track that we're in a BIRT event
            } else if (tag === "DEAT") {
                currentRecord.data.deathData = {};
                nameBeingProcessed = false;
                currentEventType = "DEAT"; // Track that we're in a DEAT event
            } else {
                // Any other level 1 tag ends the current event
                nameBeingProcessed = false;
                currentEventType = null;
            }
        }
        // Parse NAME subelements (TYPE, GIVN, SURN)
        else if (currentRecord && currentRecord.type === "INDI" && level === 2 && nameBeingProcessed) {
            if (tag === "TYPE") {
                // Track name type - prefer birth names
                if (value === "birth" && !currentRecord.data.isPreferredName) {
                    currentRecord.data.isPreferredName = true;
                }
            } else if (tag === "GIVN") {
                // Only update if this is the preferred name or no name yet
                if (!currentRecord.data.fullName || currentRecord.data.isPreferredName) {
                    currentRecord.data.givenName = value;
                }
            } else if (tag === "SURN") {
                // Only update if this is the preferred name or no name yet
                if (!currentRecord.data.fullName || currentRecord.data.isPreferredName) {
                    currentRecord.data.surname = value;
                    // Reconstruct fullName
                    const givenName = currentRecord.data.givenName || "";
                    currentRecord.data.fullName = `${givenName} ${value}`.trim();
                }
            } else {
                nameBeingProcessed = false;
            }
        }
        // Parse birth/death date and place details - only if we're in the right event
        else if (currentRecord && currentRecord.type === "INDI" && level === 2 && currentEventType) {
            let targetData = null;
            if (currentEventType === "BIRT" && currentRecord.data.birthData) {
                targetData = currentRecord.data.birthData;
            } else if (currentEventType === "DEAT" && currentRecord.data.deathData) {
                targetData = currentRecord.data.deathData;
            }

            if (targetData) {
                if (tag === "DATE") {
                    targetData.date = value;
                } else if (tag === "PLAC") {
                    targetData.place = value;
                }
            }
        }
        // Parse family properties
        else if (currentRecord && currentRecord.type === "FAM" && level === 1) {
            if (tag === "HUSB") {
                currentRecord.data.husband = value.replace(/@/g, "");
            } else if (tag === "WIFE") {
                currentRecord.data.wife = value.replace(/@/g, "");
            } else if (tag === "CHIL") {
                if (!currentRecord.data.children) {
                    currentRecord.data.children = [];
                }
                currentRecord.data.children.push(value.replace(/@/g, ""));
            }
            nameBeingProcessed = false;
        } else {
            nameBeingProcessed = false;
        }
    });

    console.log(`[GED Parser] Parsing complete. Found ${Object.keys(individuals).length} individuals and ${Object.keys(families).length} families.`);

    return { individuals, families };
};

/**
 * Build family tree relationships from parsed data
 */
export const buildFamilyTree = (individuals, families) => {
    const people = Object.keys(individuals).map((id) => {
        const person = individuals[id];
        return {
            id,
            ...person,
            parents: [],
            children: [],
            spouse: null,
        };
    });

    // Create a map for easy lookup
    const peopleMap = {};
    people.forEach((person) => {
        peopleMap[person.id] = person;
    });

    // Link families to individuals
    Object.keys(families).forEach((familyId) => {
        const family = families[familyId];

        // Add parents to children
        if (family.children) {
            family.children.forEach((childId) => {
                const child = peopleMap[childId];
                if (child) {
                    if (family.husband) {
                        const father = peopleMap[family.husband];
                        if (father && !child.parents.find((p) => p.id === father.id)) {
                            child.parents.push(father);
                        }
                    }
                    if (family.wife) {
                        const mother = peopleMap[family.wife];
                        if (mother && !child.parents.find((p) => p.id === mother.id)) {
                            child.parents.push(mother);
                        }
                    }
                }
            });
        }

        // Add children to parents
        if (family.husband) {
            const husband = peopleMap[family.husband];
            if (husband && family.children) {
                family.children.forEach((childId) => {
                    const child = peopleMap[childId];
                    if (child && !husband.children.find((c) => c.id === child.id)) {
                        husband.children.push(child);
                    }
                });
            }
        }

        if (family.wife) {
            const wife = peopleMap[family.wife];
            if (wife && family.children) {
                family.children.forEach((childId) => {
                    const child = peopleMap[childId];
                    if (child && !wife.children.find((c) => c.id === child.id)) {
                        wife.children.push(child);
                    }
                });
            }
        }

        // Add spouse relationships
        if (family.husband && family.wife) {
            const husband = peopleMap[family.husband];
            const wife = peopleMap[family.wife];
            if (husband) husband.spouse = wife;
            if (wife) wife.spouse = husband;
        }
    });

    return people;
};

/**
 * Get all ancestors of a person (parents, grandparents, etc.)
 */
export const getAncestors = (person, visited = new Set()) => {
    if (!person || visited.has(person.id)) return new Set();

    const ancestors = new Set();
    visited.add(person.id);

    if (person.parents && person.parents.length > 0) {
        person.parents.forEach((parent) => {
            ancestors.add(parent.id);
            getAncestors(parent, visited).forEach((ancestorId) => {
                ancestors.add(ancestorId);
            });
        });
    }

    return ancestors;
};

/**
 * Get all descendants of a person (children, grandchildren, etc.)
 */
export const getDescendants = (person, visited = new Set()) => {
    if (!person || visited.has(person.id)) return new Set();

    const descendants = new Set();
    visited.add(person.id);

    if (person.children && person.children.length > 0) {
        person.children.forEach((child) => {
            descendants.add(child.id);
            getDescendants(child, visited).forEach((descendantId) => {
                descendants.add(descendantId);
            });
        });
    }

    return descendants;
};

/**
 * Check if two people share ancestors (for deduplication in marriage scenarios)
 */
export const getSharedAncestors = (person1, person2) => {
    const ancestors1 = getAncestors(person1);
    const ancestors2 = getAncestors(person2);

    const shared = new Set();
    ancestors1.forEach((ancestorId) => {
        if (ancestors2.has(ancestorId)) {
            shared.add(ancestorId);
        }
    });

    return shared;
};

/**
 * Get root ancestors (people with no parents) to use as tree roots
 */
export const getRootAncestors = (people) => {
    return people.filter((person) => !person.parents || person.parents.length === 0);
};

/**
 * Get the most relevant root person for the tree
 * Usually the earliest ancestor or person with most descendants
 */
export const getMostRelevantRoot = (people) => {
    const roots = getRootAncestors(people);

    if (roots.length === 0) return people[0] || null;
    if (roots.length === 1) return roots[0];

    // Return the one with most descendants
    let maxDescendants = -1;
    let mostRelevant = roots[0];

    roots.forEach((root) => {
        const descendants = getDescendants(root).size;
        if (descendants > maxDescendants) {
            maxDescendants = descendants;
            mostRelevant = root;
        }
    });

    return mostRelevant;
};

/**
 * Extract photo path from GEDCOM OBJE record
 */
export const extractPhotoPath = (gedContent, photoId) => {
    if (!photoId || !gedContent) return null;

    const lines = gedContent.split("\n");
    let inObject = false;
    let photoPath = null;

    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.includes(`@${photoId}@ OBJE`)) {
            inObject = true;
        } else if (inObject && line.includes("1 FILE")) {
            const pathMatch = line.match(/FILE\s+(.+)/);
            if (pathMatch) {
                let filePath = pathMatch[1].trim();
                // Extract just the filename
                filePath = filePath.split("\\").pop().split("/").pop();
                photoPath = `/familytreedata/photos/${filePath}`;
                break;
            }
        } else if (inObject && line.startsWith("0")) {
            break;
        }
    }

    return photoPath;
};
